import { useState, useEffect, useRef, useContext } from "react";
import { Chart } from "react-chartjs-2";
import { Chart as Chart2 } from "chart.js";
import { gql, useLazyQuery } from "@apollo/client";
import { Entorno } from "../../../utils/constants";
import { getItemHexColor, mergeKeyData } from "../../../utils/functions";
import Chainz from "../../../dictionaries/Chainz.json";
import { colProperties, Spinner, SpinnerSize } from "@fluentui/react";
import { GraphNotCompatible } from "../../../components/GraphNotCompatible";
import AppContext from "../../../AppContext";
import { getMedidaSQL } from "../../../utils/functions";
import ChartjsPluginStacked100 from "chartjs-plugin-stacked100";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import i18n from "../../../config/i18n";
Chart2.register(ChartjsPluginStacked100);
Chart2.register(ChartDataLabels);

export type IAnnotation = {
  label: string;
  type: IAnnotationType;
  value: number;
};
type IAnnotationType =
  | "mercado"
  | "especialidad"
  | "segmento"
  | "other"
  | "total"
  | "desglose1"
  | "desglose2";
export type IBarPropsData = {
  datalabels: string[];
  labels: string[];
  values: number[][];
  colors: string[];
  annotation: IAnnotation[];
  series: any;
  horizontal?: boolean;
  min?: number;
  max?: number;
  stepSize?: number;
};
export type IBarProps = {
  data: IBarPropsData;
  hue: number;
  annotation: string;
  percentages?: boolean;
};

const isPT =
  typeof window !== "undefined" &&
  (window.location.host.includes("pt") ||
    window.location.host.includes("localhost"));

const renombrarCabecera = (nombre: string) => {
  if (
    !isPT &&
    (nombre.toLowerCase().endsWith("c c y resto") ||
      nombre.toLowerCase().endsWith("cc y resto") ||
      nombre.toLowerCase().endsWith("c c") ||
      nombre.toLowerCase().endsWith("cc"))
  ) {
    return nombre
      .replace(/c c y resto$/i, "CC")
      .replace(/cc y resto$/i, "CC")
      .replace(/c c$/i, "CC")
      .replace(/cc$/i, "CC");
  }
  if (
    isPT &&
    (nombre.toLowerCase().endsWith("c c") ||
      nombre.toLowerCase().endsWith("cc"))
  ) {
    return nombre
      .replace(/c c$/i, "CC y Resto")
      .replace(/cc$/i, "CC y Resto");
  }
  return nombre;
};

const getUbicacionLabel = (key: string) => {
  if (key === "Total" || key === "U0") return "Ubicación Total";
  if (
    key === "CC" ||
    key === "U3" ||
    key === "Centro Comercial Y Resto"
  )
    return "CC y Resto";
  if (key === "Resto" || key === "U4") return "Resto";
  if (key === "Calle" || key === "U1") return "Calle";
  return key;
};

const getRegionLabel = (key: string, leverKeys: any) => {
  return leverKeys.Region[key] || key;
};

const getLabelUbicacionTraducida = (key: string, isPT: boolean) => {
  if (key === "CC") return isPT ? t("CcResto") : t("Cc");
  if (key === "Resto") return t("Resto");
  if (key === "Calle") return t("Street");
  if (key === "Total") return t("Total");
  return t(key);
};

const getLabelSerieTraducida = (
  name: string,
  leverType: number,
  isPT: boolean,
  leverKeys: any
) => {
  if (name === "Local") {
    return t("On-premises");
  }
  if (leverType === 4) {
    return getLabelUbicacionTraducida(name, isPT);
  } else if (leverType === 3) {
    return getRegionLabel(name, leverKeys);
  }
  return t(name);
};

export const ComparadorCuotaHorizontal = ({
  variables,
  hue,
  annotation,
  percentages,
  legend,
  updateLegend,
  isMaximized,
  updateExportData,
  showLegend,
}: any) => {
  const { t } = useTranslation();
  let statusRef = useRef<any>([]);
  const graphRef = useRef<any>(null);
  let appContext = useContext(AppContext);
  const { semanas, filters, user } = useContext(AppContext);

  const [normalize, setNormalize] = useState(true);

  let currentFilters = useRef<any>(null);

  const medida = getMedidaSQL(filters.medidas);
  let entorno =
    filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

  let leverTypeKeys: any = [
    "Totales",
    "Canal",
    "Momento",
    "Region",
    "Ubicacion",
  ];

  const isPT =
    typeof window !== "undefined" &&
    (window.location.host.includes("pt") ||
      window.location.host.includes("localhost"));

  let leverKeys = {
    Canal: { C0: "Total", C1: "Local", C2: "TakeAway", C3: "Delivery" },
    Momento: {
      M0: "Total",
      M1: "Mañanas",
      M2: "Tardes",
      M3: "Comidas (L-J)",
      M4: "Cenas (L-J)",
      M5: "Comidas (V-D)",
      M6: "Cenas (V-D)",
    },
    Region: isPT
      ? {
          Z0: "Total",
          Z1: "Lisboa",
          Z2: "Porto",
          Z3: t("North"),
          Z4: t("Center"),
          Z5: "Alenteho",
          Z6: "Algarve",
        }
      : {
          Z0: "Total",
          Z1: "Madrid",
          Z2: "Barcelona",
          Z3: t("North"),
          Z4: t("Center"),
          Z5: t("Northeast"),
          Z6: t("South"),
        },
    Ubicacion: isPT
      ? { U0: "Total", U1: "Calle", U3: "CC", U4: "Resto" }
      : { U0: "Total", U1: "Calle", U3: "CC", U4: "Resto" },
  };

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
      $misdatos: Int
      $leverType: String
      $lever: String
    ) {
      graph(
        Graph: "Mi cuota comparador"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
          misdatos: $misdatos
          LeverType: $leverType
          Lever: $lever
        }
      )
    }
  `;

  const [graphData, setGraphData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string>(
    `Se ha producido un error recuperando los datos`
  );
  const [isCompatible, setCompatible] = useState<boolean>(true);

  const [fetchData, { loading, error, data }] = useLazyQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (
      !currentFilters?.current ||
      (currentFilters?.current &&
        (filters.segmentType !== currentFilters?.current.segmentType ||
          filters.segment !== currentFilters?.current.segment))
    ) {
      let leverTypeStr = "";
      let leverStr = "";
      fetchData({
        variables: {
          yr1: semanas[0].year,
          wk1: semanas[0].week,
          yr2: semanas[1].year,
          wk2: semanas[1].week,
          var: filters?.variables || 0,
          segment:
            filters.segmentType === 0
              ? filters?.segmentos[filters?.segment]
              : filters?.especialidades[filters?.segment],
          segmentType: filters.segmentType,
          misdatos: 0,
          leverType: leverTypeStr || "",
          lever: leverStr || "",
        },
      });
    }
    currentFilters.current = { ...filters };
  }, []);

  useEffect(() => {
    let leverTypeStr = "";
    let leverStr = "";

    fetchData({
      variables: {
        yr1: semanas[0].year,
        wk1: semanas[0].week,
        yr2: semanas[1].year,
        wk2: semanas[1].week,
        var: filters?.variables,
        segment:
          filters.segmentType === 0
            ? filters?.segmentos[filters?.segment] || filters?.segmentos[1]
            : filters?.especialidades[filters?.segment] ||
              filters?.especialidades[1],
        segmentType: filters.segmentType,
        misdatos: filters?.misdatos,
        leverType: leverTypeStr || "",
        lever: leverStr || "",
      },
    });
  }, [semanas, filters.segmentType, filters.segment, filters.misdatos]);

  useEffect(() => {
    if (data?.graph) {
      const medida = getMedidaSQL(filters.medidas);
      if (data?.graph?.Error || medida === "TicketMedio") {
        setCompatible(false);
        setSecondError(t("Graph-not-compatible"));
        return;
      }
      if (Object.values(data?.graph[0]).length <= 1) {
        setSecondError(
          t("Chain-not-represented-in-segment")
        );
      } else {
        setSecondError(null);
      }

      setCompatible(true);

      let entorno =
        filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

      let fetchedData: any = data?.graph;

      if (!fetchedData) throw new Error(errorMsg);
      if (fetchedData?.Error) throw new Error(`${data.Error}`);

      let labels = fetchedData?.map((d: any) => Object.keys(d)[0]);
      if (labels.length < 3)
        throw new Error(
          t("Chain-not-represented-in-segment")
        );

      let datalabels = fetchedData[0][labels[0]].map(
        (d: { Descripcion: string }) => d.Descripcion
      );

      let dataName = fetchedData[0][labels[0]].map(
        (d: { Name: string }) => d.Name
      );

      let values: any = [];
      let series: any = [];
      let values2: any = {};

      let segmentsArray: any = [];
      if (filters.variables === 0) {
        segmentsArray = datalabels.filter(
          (item: string) => item?.indexOf("sin m") > -1
        );
      } else {
        segmentsArray = datalabels.filter(
          (item: string) =>
            item?.substring(0, 4) ===
            filters.segmentos[filters.variables].substring(0, 4)
        );
      }
      let myDataArray: any = [];
      let cadenas = datalabels.filter(
        (item: string) => item?.substring(0, 4) === "cad_"
      );
      myDataArray = [...cadenas];

      let filteredLabelsInitial: any = [];
      segmentsArray.map((item: string) => filteredLabelsInitial.push(item));
      myDataArray.map((item: string) => filteredLabelsInitial.push(item));

      values2 = filteredLabelsInitial.map((item: string) => {
        return { [item]: [] };
      });

      try {
        for (let i = 0; i < filteredLabelsInitial.length; i++) {
          let valueSub: any = [];
          fetchedData.map((dat: any): void => {
            let tempArray: any = [...Object.values(dat)];
            let index = tempArray[0].findIndex((item2: any) => {
              return (
                typeof item2["Descripcion"] === "string" &&
                item2["Descripcion"].substring(0, 7) ===
                  filteredLabelsInitial[i].substring(0, 7)
              );
            });

            let arrayOfItems = tempArray[0][index];
            let valueSub2 = arrayOfItems.Item[medida][entorno];
            let index2 = values2.findIndex((item2: any) => {
              return (
                Object.keys(item2)[0].substring(0, 7) ===
                filteredLabelsInitial[i].substring(0, 7)
              );
            });
            values2[index2][filteredLabelsInitial[i]].push(valueSub2);

            if (filters.leverType === 0) {
              valueSub.push(arrayOfItems?.Item[medida][entorno].Totales);
            } else {
              let leverKey: string = leverTypeKeys[filters.leverType || 0];
              let leverSubKey =
                (leverKeys[leverKey as keyof typeof leverKeys] as {
                  [key: number]: string;
                })[filters.lever || 0];
              valueSub.push(
                arrayOfItems?.Item[medida][entorno][leverKey][leverSubKey]
              );
            }
          });
          values.push(valueSub.flat().reverse());
        }
      } catch {
        setCompatible(false);
      }

      const ubiLabel = (label: string) => getUbicacionLabel(label);
      let validIdx: number[];
      if (filters.leverType === 4) {
        if (isPT) {
          validIdx = labels
            .map((lbl: string) => ubiLabel(lbl))
            .map((lbl: string, idx: number) => ({ lbl, idx }))
            .filter(({ lbl }: { lbl: string }) => lbl !== "Resto")
            .map(({ idx }: { idx: number }) => idx);
        } else {
          validIdx = labels.map((lbl: string, idx: number) => idx);
        }
      } else if (filters.leverType === 4 && filters.lever === 3) {
        validIdx = labels
          .map((lbl: string) => ubiLabel(lbl))
          .map((lbl: string, idx: number) => ({ lbl, idx }))
          .filter(({ lbl }: { lbl: string }) => lbl === "Resto")
          .map(({ idx }: { idx: number }) => idx);
      } else {
        validIdx = labels.map((lbl: string, idx: number) => idx);
      }

      const finalLabels = validIdx.map((idx) => labels[idx])
        .map((label: string) => i18n.language === 'en' ? label.replace(/^s-/, 'w-') : label);
      const finalDatalabels = validIdx.map((idx) => datalabels[idx]);
      const finalDataNames = validIdx.map((idx) => dataName[idx]);
      const finalValues = validIdx.map((idx) => values[idx]);
      const finalSeries = validIdx.map((idx, i) => {
        let color = "#ffffff";
        const chainIndex = Object.keys(Chainz).findIndex(
          (chainItem) => chainItem === finalDataNames[i]
        );
        if (chainIndex !== -1) {
          color = Chainz[finalDataNames[i] as keyof typeof Chainz].color;
        }
        return {
          name: renombrarCabecera(ubiLabel(finalLabels[i])),
          color,
          data: finalValues[i],
        };
      });

      setGraphData({
        datalabels: finalDatalabels,
        labels: finalLabels,
        values: finalValues,
        series: finalSeries,
        values2: values2,
      });

      if (updateExportData) {
        currentFilters.current = { ...filters };
        let exportData = getExportData(
          fetchedData,
          finalDatalabels,
          finalLabels,
          semanas[0].year === 2019,
          currentFilters.current,
          leverTypeKeys,
          leverKeys,
          isPT
        );
        updateExportData(exportData);
      }

      return;
    }
  }, [data, filters, errorMsg]);

  useEffect(() => {
    //@ts-ignore -- Limitación de apexcharts?
    if (graphRef.current && graphRef.current.props.series.length > 5) {
      data.series.forEach(
        (serie: { name: string; data: number[] }, i: number) => {
          if (i > 5) graphRef?.current?.chart.hideSeries(serie.name);
        }
      );
    }
  }, [data]);

  const newLegendClickHandler = function (
    e: any,
    legendItem: any,
    legend: any
  ) {
    const index = legendItem.datasetIndex;
    const ci = legend.chart;

    if (ci.isDatasetVisible(index)) {
      ci.hide(index);
      legendItem.hidden = true;
    } else {
      ci.show(index);
      legendItem.hidden = false;
    }
    ci.update();

    // UpdateStatus
    let status = statusRef.current;
    if (status) {
      status[index].hidden = !status[index].hidden;
      //updateLegend(status);
    }
  };

  const newLegendHoverHandler = function (
    e: any,
    legendItem: any,
    legend: any
  ) {
    const index = legendItem.datasetIndex;
    const ci = legend.chart;
    setHoveredIndex(index);
    ci.update();
  };

  const newLegendLeaveHandler = function (
    e: any,
    legendItem: any,
    legend: any
  ) {
    const ci = legend.chart;
    setHoveredIndex(-1);
    ci.update();
  };

  let options = {
    responsive: true,
    tension: 0.4,
    animation: {
      duration: 400,
    },
    plugins: {
      datalabels: {
        color: showLegend ? "white" : "transparent",
        weight: "bold",
        formatter: function (value: any, context: any) {
          const data = context.chart.data;
          const datasetIndex = context.datasetIndex;
          const index = context.dataIndex;
          try {
            if (filters.lever > 0) {
              if (data?.originalData) {
                return `${data.originalData[filters.lever - 1][index]?.toFixed(
                  1
                )}%`;
              }
              return `${data.datasets[0].data[index]?.toFixed(1)}%`;
            } else if (filters.leverType > 0) {
              if (data?.originalData) {
                return `${data.originalData[datasetIndex][index].toFixed(1)}%`;
              }
              return `${data.datasets[0].data[index]?.toFixed(1)}%`;
            } else {
              return "";
            }
          } catch {
            return `${data.datasets[0].data[index]?.toFixed(1)}%`;
          }
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => {
            const data = tooltipItem.chart.data;
            const datasetIndex = tooltipItem.datasetIndex;
            const index = tooltipItem.dataIndex;
            const datasetLabel = data.datasets[datasetIndex].label || "";
            // Usar renombrarCabecera para los tooltips también
            const displayLabel = renombrarCabecera(datasetLabel);
            if (filters.lever > 0) {
              if (data.originalData) {
                return `${displayLabel}: ${data.originalData[filters.lever - 1][
                  index
                ].toFixed(1)}%`;
              }
              return `${data.datasets[0].data[index]?.toFixed(1)}%`;
            } else {
              if (data.originalData) {
                return `${displayLabel}: ${data.originalData[datasetIndex][
                  index
                ].toFixed(1)}%`;
              } else {
                return `${data.datasets[0].data[index]?.toFixed(1)}%`;
              }
            }
          },
        },
      },
      stacked100: {
        enable: filters.lever === 0 ? true : false,
        replaceTooltipLabel: filters.lever === 0 ? true : false,
      },
      legend: {
        reverse: false,
        position: "bottom" as const,
        onClick: newLegendClickHandler,
        onHover: newLegendHoverHandler,
        onLeave: newLegendLeaveHandler,
        labels: {
          color: "rgb(255, 255, 255, 0.9)",
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      title: {
        display: false,
        text: "Mi tendencia",
      },
    },

    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
      },
      y: {
        grid: {
          display: true,
          drawBorder: false,
          drawOnChartArea: true,
          drawTicks: true,
          tickColor: "#26292A",
          color: "#26292A",
        },
        ticks: {
          callback: function (value: any) {
            return value.toFixed(0) + "%";
          },
        },
      },
    },
  };

  if (filters.lever === 0) {
    options = {
      ...options,
      scales: {
        ...options.scales,
        y: {
          ...options.scales.y,
          /*@ts-ignore*/
          max: 100,
        },
      },
    };
  }

  let emptyData = {
    labels: [],
    datasets: [],
  };

  let [dataMercado, setDataMercado] = useState<any>(emptyData);
  let [dataYo, setDataYo] = useState<any>(emptyData);
  let [dataTest, setDataTest] = useState<any>(emptyData);
  let [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  let [secondError, setSecondError] = useState<any>(null);

  useEffect(() => {
    if (
      !graphData ||
      graphData?.labels?.length === 0 ||
      graphData?.series?.length === 0
    )
      return;

    setDataMercado(emptyData);
    setDataYo(emptyData);

    let graphDataGrouped: any = [];
    let datasetLabels: any = [];
    graphData.values2.map((item: any) => {
      let keys = Object.keys(item);
      graphDataGrouped.push({
        [keys[0]]: [],
      });
    });

    if (graphDataGrouped.length <= 1) {
      setSecondError(
        t("Chain-not-represented-in-segment")
      );
    } else {
      setSecondError(null);
    }
    if (filters.leverType === 0) {
      graphData.values2.forEach((item: any, index: number) => {
        let key = Object.keys(item)[0];
        datasetLabels.push(key);
        let collection = item[key];
        let collectionKeys = Object.keys(collection[0]);
        datasetLabels = [...collectionKeys];
        let subseries: any = [];

        collectionKeys.forEach((key: any) => {
          if (key !== "Totales") {
            let newSubserie = {
              name: key,
              data: collection
                .map((item: any) => item[key].Total || item?.Totales)
                .reverse()
                .flat(),
              color: "#ffffff",
            };
            subseries.push(newSubserie);
          }
        });

        let currentDataSet: any = [];
        subseries.forEach(
          (
            serie: {
              name: string;
              data: number[];
              color: string;
              labels: string[];
            },
            idx: number
          ) => {
            let color: string = serie.color;
            color = getItemHexColor("comparator", idx);
            let labelTraducido = getLabelSerieTraducida(
              serie.name,
              filters.leverType,
              isPT,
              leverKeys
            );
            currentDataSet.push({
              type: "bar",
              label: labelTraducido,
              data: serie.data,
              stack: true,
              borderColor:
                hoveredIndex !== idx && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              backgroundColor:
                hoveredIndex !== idx && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              hidden: statusRef?.current[idx]?.hidden || false,
            });
          }
        );

        let data4: any = {
          labels: [...graphData.labels].reverse(),
          datasets: currentDataSet,
        };
        if (index === 0) {
          setDataMercado(data4);
        } else {
          setDataYo(data4);
        }

        setDataTest(graphDataGrouped);
      });
    } else {
      let subLever = leverTypeKeys[filters.leverType || 0];

      graphData.values2.forEach((item: any, index: number) => {
        let key = Object.keys(item)[0];
        datasetLabels.push(key);
        let collection = item[key];

        let collectionKeys = Object.keys(collection[0][subLever]);
        collectionKeys = collectionKeys.filter((item: any) => item !== "Total");
        datasetLabels = [...collectionKeys];

        let subseries: any = [];

        collectionKeys.forEach((key: any) => {
          if (
            key !== "Totales" &&
            key !== "Concesiones" &&
            key !== "Otros" &&
            !(filters.leverType === 4 && isPT && key === "Resto")
          ) {
            let newSubserie = {
              name:
                filters.leverType === 3
                  ? getRegionLabel(key, leverKeys)
                  : key,
              data: collection
                .map((item: any) => item[subLever][key])
                .reverse()
                .flat(),
              color: "#ffffff",
            };

            if (filters.lever === 0) {
              subseries.push(newSubserie);
            } else {
              let subkeys: any =
                Object.values(leverKeys)[filters.leverType - 1];

              if (
                subkeys[Object.keys(subkeys)[filters.lever]] ===
                newSubserie.name
              ) {
                subseries.push(newSubserie);
              }
            }
          }
        });

        let currentDataSet: any = [];
        subseries.forEach(
          (
            serie: {
              name: string;
              data: number[];
              color: string;
              labels: string[];
            },
            idx: number
          ) => {
            let color: string = serie.color;
            color = getItemHexColor("comparator", idx);
            let labelTraducido = getLabelSerieTraducida(
              serie.name,
              filters.leverType,
              isPT,
              leverKeys
            );
            currentDataSet.push({
              type: "bar",
              label: labelTraducido,
              data: serie.data,
              stack: true,
              borderColor:
                hoveredIndex !== idx && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              backgroundColor:
                hoveredIndex !== idx && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              hidden: statusRef?.current[idx]?.hidden || false,
            });
          }
        );

        let data4: any = {
          labels: [...graphData.labels].reverse(),
          datasets: currentDataSet,
        };
        if (index === 0) {
          setDataMercado(data4);
        } else {
          setDataYo(data4);
        }

        setDataTest(graphDataGrouped);
      });
    }
  }, [graphData, hoveredIndex, legend]);

  const [firstTitle, setFirstTitle] = useState<string>(t("Market-excluding-me"));
  useEffect(() => {
    if (filters.segment === 0) {
      setFirstTitle(t("Market-excluding-me"));
    } else if (filters.segmentType === 0 && filters.segment === 1) {
      setFirstTitle(t("QSR-excluding-me"));
    } else if (filters.segmentType === 0 && filters.segment === 2) {
      setFirstTitle(t("Casual-excluding-me"));
    } else {
      setFirstTitle(t("Cafe-excluding-me"));
    }
  }, [filters.segment, filters.segmentType]);
  return (
    <div className="chart-container">
      {dataMercado && (
        <div className="chart-wrapper">
          {updateExportData && <h1>{firstTitle}</h1>}
          <Chart
            type="bar"
            data={
              isCompatible &&
              !loading &&
              !secondError &&
              filters.leverType > 0 &&
              !(filters.entorno === Entorno.Comparable && filters.leverType > 0)
                ? dataMercado
                : emptyData
            }
            options={options}
          />
          {isCompatible && !loading && filters.segment === 123 && (
            <GraphNotCompatible>
              Seleccione un segmento diferente a Total
            </GraphNotCompatible>
          )}
          {isCompatible && !loading && secondError && (
            <GraphNotCompatible>{secondError}</GraphNotCompatible>
          )}
          {!isCompatible && !loading && (
            <GraphNotCompatible>
              {secondError ||
                `La configuración de filtros seleccionada no es compatible con este
              gráfico.`}
            </GraphNotCompatible>
          )}
          {filters.leverType === 0 && !loading && !secondError && (
            <GraphNotCompatible>
              {secondError || t("Select-Variable")}
            </GraphNotCompatible>
          )}
          {filters.entorno === Entorno.Comparable &&
            filters.leverType > 0 &&
            !loading && (
              <GraphNotCompatible>
                {t("Graph-not-support")}
              </GraphNotCompatible>
            )}
        </div>
      )}
      {dataYo && isMaximized && (
        <div className="chart-wrapper">
          <br />
          {updateExportData && <h1>{t("My-Position")}</h1>}
          <Chart
            type="bar"
            data={
              isCompatible &&
              !loading &&
              !secondError &&
              filters.leverType > 0 &&
              !(filters.entorno === Entorno.Comparable && filters.leverType > 0)
                ? dataYo
                : emptyData
            }
            options={options}
          />
          {isCompatible && !loading && secondError && (
            <GraphNotCompatible>{secondError}</GraphNotCompatible>
          )}
          {!isCompatible && !loading && (
            <GraphNotCompatible>
              {secondError ||
                `La configuración de filtros seleccionada no es compatible con este
              gráfico.`}
            </GraphNotCompatible>
          )}
          {filters.leverType === 0 && !loading && !secondError && (
            <GraphNotCompatible>
              {secondError || t("Select-Variable")}
            </GraphNotCompatible>
          )}
          {filters.entorno === Entorno.Comparable &&
            filters.leverType > 0 &&
            !loading && (
              <GraphNotCompatible>
                {t("Graph-not-support")}
              </GraphNotCompatible>
            )}
        </div>
      )}
      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
          <p>{t("Extracting-data")}</p>
        </div>
      )}
    </div>
  );
};


const norm = (s: string) =>
  (s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");


const safeT = (t: (k: string, o?: any) => string, key: string, def?: string) => {
  const val = t(key);
  return val === key ? (def ?? key) : val;
};


const translateTopLabel = (label: string, t: (k: string, o?: any) => string) => {
  if (!label) return label;
  const n = norm(label);

  if (n === "mercado sin mi")
    return safeT(t, "translation:Market-excluding-me", "Market excluding me") as string;
  if (n === "mercado")
    return safeT(t, "translation:Market", "Market") as string;
  if (n === "periodo")
    return safeT(t, "translation:Period", "Period") as string;


  const direct = t(label);
  return direct !== label ? (direct as string) : label;
};


const translateHeaderLabel = (header: string, t: (k: string) => string) => {
  if (!header) return header;
  return header.replace(/\bPeriodo\b/gi, safeT(t as any, "translation:Period", "Period"));
};


const translateTopKey = (key: string, t: (k: string, o?: any) => string) => {
  const k = norm(key);

  if (k === "totales" || k === "total") return safeT(t, "translation:Totals", "Totals");
  if (k === "canal") return safeT(t, "translation:Channel", "Channel");
  if (k === "momento") return safeT(t, "translation:Moment", "Moment");
  if (k === "region" || k === "región") return safeT(t, "translation:Region", "Region");
  if (k === "ubicacion" || k === "ubicación") return safeT(t, "translation:Location", "Location");
  if (k === "mercado") return safeT(t, "translation:Market", "Market");
  if (k === "mercado sin mi") return safeT(t, "translation:Market-excluding-me", "Market excluding me");
  if (k === "periodo") return safeT(t, "translation:Period", "Period");

  const direct = t(key);
  return direct !== key ? direct : key;
};

const esColumnaExcluida = (nombre: string) => {
  const excluidos = ["concesiones", "otros"];
  const lower = nombre.toLowerCase().trim();
  return excluidos.some((ex) => lower.endsWith(ex));
};

const getExportData = (
  data: any,
  datalabels: any[],
  labels: any,
  isLeap: boolean,
  currentFilters: any,
  leverTypeKeys: any,
  leverKeys: any,
  isPT: boolean,
  t?: (k: string, o?: any) => string
) => {
  
  const tt = t ?? ((k: string) => k);

  const translateMed = (med: string) =>
    med === "Ventas"
      ? safeT(tt, "translation:Sales", "Sales")
      : med === "Tickets"
      ? safeT(tt, "translation:Tickets", "Tickets")
      : safeT(tt, "translation:Average-ticket", "Average ticket");

  const translateSubUbicacion = (val: string) => {
    if (val === "CC") return isPT ? safeT(tt, "translation:CcResto", "CcResto") : safeT(tt, "translation:Cc", "Cc");
    if (val === "Calle") return safeT(tt, "translation:Street", "Street");
    if (val === "Resto") return safeT(tt, "translation:Resto", "Resto");
    if (val === "Total") return safeT(tt, "translation:Totals", "Totals");
    const direct = tt(val);
    return direct !== val ? direct : val;
  };

  const translateMomento = (key: string) => {
    if (key === "Total") return safeT(tt, "translation:Total", "Total");
    const direct = tt(key);
    return direct !== key ? direct : key;
  };

  const translateRegion = (code: string) => {
    const n = (code || "").toUpperCase();
    const map: Record<string, string> = isPT
      ? { Z0: "Total", Z1: "Lisboa", Z2: "Porto", Z3: safeT(tt, "translation:North", "North"), Z4: safeT(tt, "translation:Center", "Center"), Z5: "Alenteho", Z6: "Algarve" }
      : { Z0: "Total", Z1: "Madrid", Z2: "Barcelona", Z3: safeT(tt, "translation:North", "North"), Z4: safeT(tt, "translation:Center", "Center"), Z5: safeT(tt, "translation:Northeast", "Northeast"), Z6: safeT(tt, "translation:South", "South") };
    const label = map[n] ?? code;
    if (norm(label) === "total") return safeT(tt, "translation:Total", "Total");
    return label;
  };

  const scopes: string[] = currentFilters.entorno === 0 ? ["Totales"] : ["Comparables"];

  const test = Object.values(data[0]);
  const test2: string[] = (test[0] as { Descripcion: string }[]).map((item) => item?.Descripcion);

  const exportData: any[] = [];

  for (let i = 0; i < test2.length; i++) {
    const tempVals: Record<string, any> = {};

    ["Ventas", "Tickets"].forEach((med) => {
      const medLabel = translateMed(med);

      scopes.forEach((ent: string) => {
        if (currentFilters.leverType === 0) {
          const tempVal = data
            .map((dat: Record<string, any>) => Object.values(dat).map((d: any) => d?.[i]?.Item?.[med]?.[ent]))
            .flat()
            .reverse();

          const payload: Record<string, any[]> = {};
          tempVal.forEach((it: Record<string, any>) => {
            if (!it) return;
            Object.keys(it).forEach((k) => {
              if (!payload[k]) payload[k] = [it[k]];
              else payload[k].push(it[k]);
            });
          });

          Object.keys(payload).forEach((key: string) => {
            if (esColumnaExcluida(key)) return;
            const exportKey = translateTopKey(key, tt);
            const header = translateHeaderLabel(renombrarCabecera(`${medLabel} ${exportKey}`), tt as any);
            tempVals[header] = { ...mergeKeyData(labels, payload[key]) };
          });
        } else if (currentFilters.leverType === 4) {
          const palanca = leverTypeKeys[currentFilters.leverType];
          const subUbicaciones = isPT
            ? [safeT(tt, "translation:Total", "Total"), safeT(tt, "translation:Street", "Street"), safeT(tt, "translation:CcResto", "CcResto")]
            : [safeT(tt, "translation:Total", "Total"), safeT(tt, "translation:Street", "Street"), safeT(tt, "translation:Cc", "Cc"), safeT(tt, "translation:Resto", "Resto")];

          subUbicaciones.forEach((subPalanca) => {
            const real =
              subPalanca === safeT(tt, "translation:CcResto", "CcResto")
                ? "CC"
                : subPalanca === safeT(tt, "translation:Cc", "Cc")
                ? "CC"
                : subPalanca === safeT(tt, "translation:Resto", "Resto")
                ? "Resto"
                : subPalanca === safeT(tt, "translation:Street", "Street")
                ? "Calle"
                : subPalanca === safeT(tt, "translation:Total", "Total")
                ? "Total"
                : subPalanca;

            const tempVal = data
              .map((dat: Record<string, any>) => Object.values(dat).map((d: any) => d?.[i]?.Item?.[med]?.[ent]?.[palanca]?.[real]))
              .flat()
              .reverse();

            const row: { [k: string]: number } = {};
            labels.forEach((lab: string | number, idx: string | number) => (row[lab] = tempVal[idx] ?? 0));

            const header = translateHeaderLabel(`${medLabel} ${subPalanca}`, tt as any);
            tempVals[header] = row;
          });
        } else {
          const palanca = leverTypeKeys[currentFilters.leverType];
          const tempVal = data
            .map((dat: Record<string, any>) => Object.values(dat).map((d: any) => d?.[i]?.Item?.[med]?.[ent]?.[palanca]))
            .flat()
            .reverse();

          const payload: Record<string, any[]> = {};
          tempVal.forEach((it: Record<string, any>) => {
            if (!it) return;
            Object.keys(it).forEach((k) => {
              if (!payload[k]) payload[k] = [it[k]];
              else payload[k].push(it[k]);
            });
          });

          Object.keys(payload).forEach((key: string) => {
            if (esColumnaExcluida(key)) return;
            let subLabel: string;
            if (palanca === "Momento") subLabel = translateMomento(key);
            else if (palanca === "Ubicacion") subLabel = translateSubUbicacion(key);
            else if (palanca === "Region") subLabel = translateRegion(key);
            else subLabel = (tt(key) !== key ? tt(key) : key) as string;

            const header = translateHeaderLabel(renombrarCabecera(`${medLabel} ${subLabel}`), tt as any);
            tempVals[header] = { ...mergeKeyData(labels, payload[key]) };
          });
        }
      });
    });


    const topKey = translateTopLabel(test2[i], tt as any);
    exportData.push({ [topKey]: tempVals });
  }

  const allowed = new Set<string>([
    ...datalabels,
    ...datalabels.map((dl) => translateTopLabel(dl, tt as any)),
  ]);
  const exportDataFiltered = exportData.filter((item: Record<string, any>) =>
    allowed.has(Object.keys(item)[0])
  );

  return exportDataFiltered;
};